import { useCallback, useState } from "react";
import { getBuilderWarnings, validateFormAnswers, type BuilderDraft, type FormAnswer, type FormAnswers } from "@froppio/form-core";
import type { FormRendererComponents } from "./types";
import { FormPreviewField } from "./FormPreviewField";

export interface FormPreviewProps {
  draft: BuilderDraft;
  components: FormRendererComponents;
  showAnswerActions?: boolean;
}

export function FormPreview({ draft, components, showAnswerActions = true }: FormPreviewProps) {
  const { Button, Section } = components;
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [checked, setChecked] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [controlErrors, setControlErrors] = useState<Record<string, string>>({});
  const warnings = getBuilderWarnings(draft);
  const errors = validateFormAnswers(draft, answers);
  const hasErrors = Object.keys(errors).length > 0 || Object.keys(controlErrors).length > 0;

  const changeAnswer = useCallback((id: string, value: FormAnswer) => {
    setAnswers(previous => ({ ...previous, [id]: value }));
    setChecked(false);
  }, []);
  const changeControlError = useCallback((id: string, error: string | undefined) => {
    setControlErrors(previous => {
      if (previous[id] === error) return previous;
      const next = { ...previous };
      if (error) next[id] = error;
      else delete next[id];
      return next;
    });
  }, []);

  function renderField(id: string) {
    const field = draft.fields[id];
    if (!field) return <p key={id} role="alert" className="text-sm text-destructive">This question is missing from the definition.</p>;
    const showError = showAnswerActions ? checked : id in answers;
    return <FormPreviewField key={id} components={components} field={field} value={answers[id]} error={controlErrors[id] || (showError ? errors[id] : undefined)} onChange={changeAnswer} onControlError={changeControlError} />;
  }
  const missingFields = draft.elements.some(element => (element.type === "field" ? [element] : element.elements).some(child => !draft.fields[child.fieldId]));

  function reset() {
    setAnswers({});
    setChecked(false);
    setControlErrors({});
    setResetKey(previous => previous + 1);
  }

  return (
    <div className="space-y-6" onKeyDown={event => {
      if (event.key === "Enter" && event.target instanceof HTMLInputElement) event.preventDefault();
    }}>
      <p className="text-sm text-muted-foreground">Interactive preview only. Answers and signatures are not submitted or saved, and are discarded when you leave preview.</p>
      {warnings.length > 0 && (
        <Section title="Complete your form before using it" card>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {warnings.map((warning, index) => <li key={`${warning.id}-${index}`}>{warning.message}</li>)}
          </ul>
        </Section>
      )}
      <article key={resetKey} aria-label="Form preview" className="min-w-0 space-y-8 rounded-[20px] border border-border bg-card p-5 shadow-sm sm:p-10">
        {draft.elements.map(element => element.type === "field" ? renderField(element.fieldId) : (
          <Section key={element.id} title={element.title.trim() ? element.title : "Untitled section"}>
            {element.description && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{element.description}</p>}
            <div className="space-y-[18px]">{element.elements.map(child => renderField(child.fieldId))}</div>
            {!element.elements.length && <p className="text-sm text-muted-foreground">This section has no questions yet.</p>}
          </Section>
        ))}
        {!draft.elements.length && <p className="text-sm text-muted-foreground">No questions yet.</p>}
      </article>
      {showAnswerActions && <div className="flex flex-wrap gap-3">
        <Button type="button" onClick={() => setChecked(true)}>Check answers</Button>
        <Button type="button" variant="outline" onClick={reset}>Reset answers</Button>
      </div>}
      {showAnswerActions && checked && (
        <p role="status" className={`text-sm ${hasErrors || warnings.length || missingFields ? "text-destructive" : "text-foreground"}`}>
          {warnings.length || missingFields
            ? "The form is incomplete. Resolve the authoring warnings and any answer errors before checking again."
            : hasErrors ? "Please correct the answer errors above."
              : "All answers pass this preview check. Nothing has been submitted or saved."}
        </p>
      )}
    </div>
  );
}
